/**
 * Écarts Expert vs prééval Manager — US-10 / Lot 8.
 * delta = score Expert − score Manager (après auto-éval Expert soumise/validée).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  domainsApi,
  preevalApi,
  ApiRequestError,
  type DomainDto,
  type ApplicationDto,
  type PreevalGapRowDto,
} from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";

export function PreevalGapsPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [erp, setErp] = useState<ApplicationDto[]>([]);
  const [year, setYear] = useState(() => {
    const y = Number(searchParams.get("year"));
    return y >= 2020 && y <= 2100 ? y : new Date().getFullYear();
  });
  const [domain, setDomain] = useState(() => searchParams.get("domain") ?? "");
  const [applicationId, setErpId] = useState(() => searchParams.get("applicationId") ?? "");
  const [expertUserId] = useState(() => searchParams.get("expertUserId") ?? "");
  const [gaps, setGaps] = useState<PreevalGapRowDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [onlyDelta, setOnlyDelta] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, e] = await Promise.all([domainsApi.list(), assessmentApi.listErp()]);
        if (cancelled) return;
        setDomains(d.domains.filter((x) => x.active));
        setErp(e.applications);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await preevalApi.gaps({
          year,
          domain: domain || undefined,
          applicationId: applicationId || undefined,
          expertUserId: expertUserId || undefined,
        });
        if (!cancelled) setGaps(res.gaps);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, domain, applicationId, expertUserId]);

  const rows = onlyDelta
    ? gaps.filter((g) => g.delta != null && g.delta !== 0)
    : gaps;

  const expertName = gaps[0]?.expertName;

  return (
    <div className="sm-page">
      <PageHeader
        title={t("preeval_gaps")}
        subtitle={
          expertName
            ? `${t("preeval_gaps_sub")} — ${expertName}`
            : t("preeval_gaps_sub")
        }
        actions={
          <Link to="/preevals" className="sm-btn sm-btn-ghost">
            ← {t("preevals")}
          </Link>
        }
      />

      <div
        className="sm-card sm-card-pad"
        style={{ marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}
      >
        <label className="sm-muted">
          {t("year")}{" "}
          <input
            type="number"
            className="sm-input"
            min={2020}
            max={2100}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ width: 100, marginLeft: 6 }}
          />
        </label>
        <label className="sm-muted">
          {t("domain")}{" "}
          <select
            className="sm-input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            style={{ marginLeft: 6, minWidth: 140 }}
          >
            <option value="">{t("all")}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.code}>
                {d.code}
              </option>
            ))}
          </select>
        </label>
        <label className="sm-muted">
          {t("erp")}{" "}
          <select
            className="sm-input"
            value={applicationId}
            onChange={(e) => setErpId(e.target.value)}
            style={{ marginLeft: 6, minWidth: 140 }}
          >
            <option value="">{t("all")}</option>
            {erp.map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="sm-muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={onlyDelta}
            onChange={(e) => setOnlyDelta(e.target.checked)}
          />
          {t("preeval_gaps_only_diff")}
        </label>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <p className="sm-muted" style={{ marginBottom: 8 }}>
        {t("preeval_gaps_formula")}
      </p>

      <div className="sm-card">
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("preeval_expert_short")}</th>
                <th>{t("domain")}</th>
                <th>{t("erp")}</th>
                <th>{t("bpml_line")}</th>
                <th>Manager</th>
                <th>{t("preeval_expert_short")}</th>
                <th>Δ</th>
                <th>{t("self_assessment_short")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {gaps.length === 0
                      ? t("preeval_gaps_empty")
                      : onlyDelta
                        ? t("preeval_gaps_no_diff")
                        : t("no_data")}
                  </td>
                </tr>
              ) : (
                rows.map((g) => {
                  const abs = g.delta == null ? 0 : Math.abs(g.delta);
                  const color =
                    g.delta == null
                      ? undefined
                      : abs >= 2
                        ? "#b91c1c"
                        : abs === 1
                          ? "#c2410c"
                          : undefined;
                  return (
                    <tr key={`${g.expertUserId}-${g.bpmlLineId}-${g.applicationLabel}`}>
                      <td>{g.expertName}</td>
                      <td>{g.domain}</td>
                      <td>{g.applicationLabel}</td>
                      <td>{g.lineLabel}</td>
                      <td>{g.managerScore ?? "—"}</td>
                      <td>{g.expertScore ?? "—"}</td>
                      <td style={{ fontWeight: 600, color }}>
                        {g.delta == null ? "—" : g.delta > 0 ? `+${g.delta}` : g.delta}
                      </td>
                      <td className="sm-muted">{g.assessmentStatus ?? "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
